/**
 * SOLID Review
 * S — Strong. One concern: read a build-time env var in a way that survives running outside Vite.
 * O — N/A — a single function.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Two parameters, one of which exists solely to make the absent-env branch testable.
 * D — Strong. Depends on nothing; the env object is injected with a default.
 */
/**
 * Read a `VITE_*` variable, tolerating an environment where `import.meta.env` does not exist.
 *
 * WHY (2026-08-19, rev4 E.7). The frontend UML generator runs under Node, where `import.meta.env`
 * is undefined, and modules that read it directly threw
 * `TypeError: Cannot read properties of undefined` before the generator reached a single port. The
 * first fix guarded each call site inline — which put an `?? {}` branch into shipping code that no
 * test could reach, because `import.meta.env` is always defined under Vite and vitest. An
 * uncoverable branch is what §28.18 calls a broken signature.
 *
 * One helper, injected default, both branches reachable from a test. Migrated only the two modules
 * the generator actually needs; the other eleven direct readers can follow if they ever need to run
 * outside a bundler, and there is no value in churning them speculatively.
 */
export function readEnv(
  key: string,
  env: Record<string, string | undefined> | undefined = import.meta.env as unknown as Record<string, string | undefined>,
): string | undefined {
  return env?.[key];
}
